// utils/cambioDeCorreoPropio.ts — cambiar el correo de MI cuenta exige la contraseña actual (round 2026-10-31, S-03).
//
// Decisión del dueño («completamente, debería pedirla»): con una sesión robada, pero sin la contraseña, bastaba cambiar el correo
// propio y pedir la recuperación a la dirección nueva para quedarse con la cuenta. Ahora el servidor exige `currentPassword` cuando
// quien llama cambia el correo de SU PROPIA cuenta (en cualquier rol):
//   · sin ella            → 400 CONTRASENA_ACTUAL_REQUERIDA;
//   · incorrecta          → 403 CONTRASENA_ACTUAL_INCORRECTA (cuenta como intento fallido);
//   · demasiados intentos → 429 DEMASIADOS_INTENTOS_CONTRASENA (10 en 15 minutos por usuario, y mientras dura ni la correcta pasa).
// Un administrador que le cambia el correo a OTRA cuenta (aseadora, cliente, trabajador, otro administrador) NO la necesita: ese caso
// no pasa por acá (esas pantallas no tienen el campo).
//
// El servidor compara el correo «sin distinguir mayúsculas ni espacios»: cambiar solo «Ana@af.cl» por «ana@af.cl» no es un cambio y
// no pide la contraseña. Este archivo hace la misma comparación para pedirla (o no) exactamente cuando el servidor la va a exigir.
//
// Ronda F3b: la contraseña NO se pide mientras el correo escrito no sea un correo (vacío o a medio escribir): primero hay que
// arreglar el correo, y exigir la contraseña (con su mensaje rojo) en ese momento era pedir dos cosas a la vez.
import type { FormInstance } from "antd";
import type { ErrorApi } from "../services/apiClient";
import { llevarCampoALaVista } from "./formularioErrores";

/** Nombre del campo «Contraseña actual» en el formulario de Mi perfil. Solo vive en el formulario (memoria): nunca en un estado persistente. */
export const CAMPO_CONTRASENA_ACTUAL = "contrasenaActual";

export const CODIGO_CONTRASENA_REQUERIDA = "CONTRASENA_ACTUAL_REQUERIDA";
export const CODIGO_CONTRASENA_INCORRECTA = "CONTRASENA_ACTUAL_INCORRECTA";
export const CODIGO_DEMASIADOS_INTENTOS = "DEMASIADOS_INTENTOS_CONTRASENA";

/** La regla del campo (la contraseña falta ANTES de mandar nada al servidor). */
export const MENSAJE_CONTRASENA_REQUERIDA = "Ingresa tu contraseña actual para cambiar el correo.";
// Las dos frases de abajo son, palabra por palabra, las que responde el servidor (`backend/src/utils/contrasenaActual.js`): `apiFetch`
// ya muestra ese texto como aviso de unos segundos, y si el campo dijera otra cosa la persona leía DOS frases distintas a la vez
// (ronda F3b, «La contraseña actual es incorrecta.» arriba y «…no es correcta.» abajo). Son el respaldo para cuando la respuesta no
// trae mensaje; con mensaje, el campo muestra el del servidor tal cual (ver `marcarCampoContrasenaActual`).
export const MENSAJE_CONTRASENA_INCORRECTA = "La contraseña actual es incorrecta.";
export const MENSAJE_CONTRASENA_ESPERA = "Demasiados intentos con la contraseña actual. Espera unos minutos antes de volver a intentarlo.";

/** El correo como lo compara el servidor: sin espacios en las puntas y en minúsculas. */
export const normalizarCorreo = (valor: unknown): string => String(valor ?? "").trim().toLowerCase();

/** ¿El correo nuevo es de verdad otro (a ojos del servidor)? Solo entonces se pide la contraseña actual. */
export const cambiaElCorreo = (original: unknown, nuevo: unknown): boolean => normalizarCorreo(original) !== normalizarCorreo(nuevo);

// La MISMA expresión (y el mismo tope de 320 caracteres) con que Ant Design valida `{ type: "email" }`
// (`@rc-component/async-validator`, rule/type.js): si difiriera, el campo podría quedar sin mostrar con un correo que el formulario ya
// dio por bueno, y guardar pediría una contraseña que nadie ve. Una prueba la compara con el validador real.
const PATRON_CORREO =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9 -퟿豈-﷏ﷰ-￯]+\.)+[a-zA-Z -퟿豈-﷏ﷰ-￯]{2,}))$/;

/** ¿Lo escrito es un correo completo y bien formado (el que el formulario aceptaría)? Vacío o a medio escribir → `false`. */
export const correoEscritoEsValido = (valor: unknown): boolean => typeof valor === "string" && valor.length <= 320 && PATRON_CORREO.test(valor);

/**
 * Si el rechazo del servidor es por la contraseña actual, lo deja en el estado de error del campo (con el MISMO texto que el servidor
 * mostró como aviso: una sola frase, no dos), lleva la pantalla hasta él y le da el foco. Con la contraseña INCORRECTA además vacía lo
 * escrito (para que la persona la escriba de nuevo, no la corrija a ciegas). Devuelve `true` si lo marcó. Un 401 no pasa por acá: el
 * interceptor de `apiClient` ya cierra la sesión.
 */
export function marcarCampoContrasenaActual(form: FormInstance, error: Pick<ErrorApi, "status" | "code" | "message"> | null | undefined): boolean {
  if (!error) return false;
  // El texto que `apiFetch` mostró: el del servidor. «Error 403» es el que inventa `apiFetch` cuando la respuesta no trae ninguno.
  const delServidor = typeof error.message === "string" && error.message.trim() !== "" && !/^Error \d+$/.test(error.message.trim()) ? error.message.trim() : null;
  let marca: { name: string; errors: string[]; value?: string } | null = null;
  if (error.code === CODIGO_CONTRASENA_REQUERIDA) {
    marca = { name: CAMPO_CONTRASENA_ACTUAL, errors: [delServidor ?? MENSAJE_CONTRASENA_REQUERIDA] };
  } else if (error.code === CODIGO_CONTRASENA_INCORRECTA) {
    marca = { name: CAMPO_CONTRASENA_ACTUAL, errors: [delServidor ?? MENSAJE_CONTRASENA_INCORRECTA], value: "" };
  } else if (error.code === CODIGO_DEMASIADOS_INTENTOS || error.status === 429) {
    // Se conserva lo escrito: no es que esté mal, es que hay que esperar. Un 429 SIN código es el limitador general (su texto no es
    // de la contraseña, y puede venir en inglés): ahí manda la frase propia.
    marca = { name: CAMPO_CONTRASENA_ACTUAL, errors: [(error.code === CODIGO_DEMASIADOS_INTENTOS ? delServidor : null) ?? MENSAJE_CONTRASENA_ESPERA] };
  }
  if (!marca) return false;
  form.setFields([marca]);
  // El desplazamiento y el foco son una ayuda (y la barra de «cambios sin guardar» no debe tapar el campo): ver `llevarCampoALaVista`.
  llevarCampoALaVista(form, CAMPO_CONTRASENA_ACTUAL);
  return true;
}
